import TemplatePicker from '@/components/templates/TemplatePicker';
import { Tooltip } from '@/components/ui/tooltip';
import { HStack, IconButton } from '@chakra-ui/react';
import type { TemplateLibraryEntry } from '@fluenceur/types';
import { Plus } from 'iconoir-react';
import { memo } from 'react';

interface TemplateComposerControlsProps {
  entries: TemplateLibraryEntry[];
  selectedEntryId: string | null;
  onSelect: (entryId: string) => void;
  disabled?: boolean;
  saveDisabled?: boolean;
  isLoading?: boolean;
  showSave: boolean;
  onSave: () => void;
  saveLabel: string;
  confirmAttachmentReplacement?: boolean;
}

/**
 * Compact editor footer controls for selecting an existing template and saving
 * the current composition as a new one.
 */
const TemplateComposerControls = memo<TemplateComposerControlsProps>(
  ({
    entries,
    selectedEntryId,
    onSelect,
    disabled,
    isLoading,
    showSave,
    onSave,
    saveLabel,
    confirmAttachmentReplacement,
    saveDisabled,
  }) => {
    const attached = showSave;

    return (
      <HStack gap={0} minW={0}>
        {showSave && (
          <Tooltip content={saveLabel}>
            <IconButton
              size="xs"
              variant="outline"
              colorPalette="gray"
              flexShrink={0}
              borderColor="border"
              borderEndRadius={attached ? '0' : undefined}
              marginEnd={attached ? '-1px' : undefined}
              aria-label={saveLabel}
              onClick={onSave}
              disabled={disabled || saveDisabled}
            >
              <Plus width={14} height={14} strokeWidth={1.8} />
            </IconButton>
          </Tooltip>
        )}
        <TemplatePicker
          entries={entries}
          selectedEntryId={selectedEntryId}
          onSelect={onSelect}
          disabled={disabled || isLoading}
          attachedStart={attached}
          confirmAttachmentReplacement={confirmAttachmentReplacement}
        />
      </HStack>
    );
  }
);

TemplateComposerControls.displayName = 'TemplateComposerControls';

export default TemplateComposerControls;
